import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useRef, useState } from 'react';
import cn from 'classnames';
import { Spinner, TextButton, Tooltip, Typography, useOverlayContext } from 'seagull';
import { Edit, Import } from 'seagull/icons';
import { useLocaleKeys } from '~/i18n';
import styles from './LogoPicker.module.scss';
export function readLogo(file, onChange, onNonPNG, onFileTooBig) {
    const fileSizeLimit = 5 * 1024 * 1024;
    if (!file) {
        return;
    }
    if (!file.name.toLowerCase().endsWith('.png')) {
        return onNonPNG();
    }
    if (file.size >= fileSizeLimit) {
        return onFileTooBig();
    }
    const reader = new FileReader();
    reader.onload = (e)=>{
        const data = e.target ? {
            file,
            asDataURL: e.target.result
        } : undefined;
        onChange(data);
    };
    reader.readAsDataURL(file);
}
/** Renders a circle with a plus icon that chooses a .png file from the file-system with a clear button. */ export const LogoPicker = (param)=>{
    let { image, loading, testId, onChange, inputRef, showEditButton, editButtonLabel } = param;
    const overlayContext = useOverlayContext();
    const { t: commonTexts } = useLocaleKeys('common');
    const { t: logoPickerTexts } = useLocaleKeys('components.logoPicker');
    const logoInputRef = inputRef !== null && inputRef !== void 0 ? inputRef : useRef(null);
    const [isDraggedOver, setIsDraggedOver] = useState(false);
    const readFile = (file)=>readLogo(file, onChange, ()=>overlayContext.showErrorToast(commonTexts.toasts.invalidFileType(), 'file-type-error'), ()=>overlayContext.showErrorToast(logoPickerTexts.fileTooLarge(), 'file-size-error'));
    const onPickLogo = (event)=>{
        if (!event.target.files || !event.target.files.length) {
            return;
        }
        readFile(event.target.files[0]);
    };
    const triggerInput = ()=>logoInputRef.current && logoInputRef.current.click();
    const onDrop = (e)=>{
        e.preventDefault();
        if (e.dataTransfer.items.length && e.dataTransfer.items[0].kind === 'file') {
            const file = e.dataTransfer.items[0].getAsFile();
            readFile(file);
        }
        setIsDraggedOver(false);
    };
    const onDragOver = (e)=>{
        e.preventDefault();
        setIsDraggedOver(true);
    };
    const onDragLeave = ()=>{
        setIsDraggedOver(false);
    };
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: /*#__PURE__*/ _jsxs("div", {
            className: cn(styles.uploadBox, {
                [styles.draggedOver]: isDraggedOver,
                [styles.hasLogo]: !!image
            }),
            onDrop: onDrop,
            onDragOver: onDragOver,
            onDragLeave: onDragLeave,
            children: [
                /*#__PURE__*/ _jsx("input", {
                    className: styles.input,
                    ref: logoInputRef,
                    type: "file",
                    accept: ".png",
                    "data-testid": testId && "".concat(testId, "-input"),
                    onChange: onPickLogo
                }),
                /*#__PURE__*/ _jsx("button", {
                    type: "button",
                    className: cn(styles.add, {
                        [styles.addNewUI]: !image
                    }),
                    "data-testid": testId && "".concat(testId, "-add"),
                    disabled: loading,
                    onClick: triggerInput,
                    children: image ? /*#__PURE__*/ _jsx("img", {
                        className: styles.image,
                        alt: "Logo",
                        src: image,
                        "data-testid": testId && "".concat(testId, "-img")
                    }) : /*#__PURE__*/ _jsxs("div", {
                        className: styles.uploadContent,
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: styles.uploadIconCircle,
                                children: /*#__PURE__*/ _jsx(Import, {
                                    size: 24
                                })
                            }),
                            /*#__PURE__*/ _jsx(TextButton, {
                                bi: {
                                    name: 'upload_business_logo'
                                },
                                variant: "paragraph2",
                                weight: "light",
                                underline: "none",
                                onClick: triggerInput,
                                children: logoPickerTexts.uploadLogo()
                            }),
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraph3",
                                skin: "secondary",
                                children: logoPickerTexts.pngSizeHint()
                            })
                        ]
                    })
                }),
                loading && /*#__PURE__*/ _jsx("div", {
                    className: styles.loading,
                    children: /*#__PURE__*/ _jsx(Spinner, {
                        size: 40
                    })
                }),
                !!image && showEditButton && /*#__PURE__*/ _jsx(Tooltip, {
                    title: editButtonLabel !== null && editButtonLabel !== void 0 ? editButtonLabel : logoPickerTexts.changeLogo(),
                    placement: "right",
                    children: /*#__PURE__*/ _jsx("button", {
                        type: "button",
                        className: styles.editButton,
                        "data-testid": testId && "".concat(testId, "-edit"),
                        onClick: triggerInput,
                        "aria-label": editButtonLabel !== null && editButtonLabel !== void 0 ? editButtonLabel : logoPickerTexts.changeLogo(),
                        children: /*#__PURE__*/ _jsx(Edit, {
                            size: 16
                        })
                    })
                })
            ]
        })
    });
};
